import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import taskReducer from './taskReducer';
import {
  SpinnerArea,
  Table,
  TablePagination,
  CustomModal,
  Button,
  LinkButton,
  ConfirmModal,
} from '../../../shared/components';
import operationService from '../../../services/operationService';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import {
  TaskFilter,
  OperationTask,
  OperationTaskForm,
  CreateOperationTask,
  OperationTaskDelegateForm,
  DelegateOperationTask,
  DeleteOperationTask,
} from '../../../services/types/OperationTask';
import { Cell } from '../../../shared/components/Table';
import { dateStr, safeFind } from '../../../shared/misc/utils';
import OperationContext from './context';
import Context from '../../App/MainProvider/context';
import TaskForm from './TaskForm';
import TaskDelegateForm from './TaskDelegateForm';
import Highlight from '../Highlight';
import TaskStatus from './TaskStatus';
import NotificationContext from '../../Notifications/NotificationContext';

const Tasks: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useContext(Context);
  const isAdmin = user.roleCode === 'ADMIN';
  const isViewer = user.roleCode === 'VIEWER';
  const {
    operation: { id, number },
  } = useContext(OperationContext);
  const { service, notify } = useContext(Context);
  const { reload: reloadNotifications } = useContext(NotificationContext);

  const [showCreateNewTaskModal, setShowCreateNewTaskModal] = useState(false);
  const [showTaskDelegateModal, setShowTaskDelegateModal] = useState(false);
  const [taskState, setTaskState] = useState<OperationTaskForm | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmRemoveDelegate, setShowConfirmDelegate] = useState(false);
  const [delegateForRemove, setDelegateForRemove] = useState<number | null>(null);

  const initFilter: TaskFilter = {
    operationId: id,
    pageNumber: 1,
    sortField: 'title',
    sortDescending: false,
  };

  const [data, loading, filter, dispatch, loader, reload] = useServiceReducer(
    operationService.getTasks,
    taskReducer,
    initFilter
  );

  const pageSize = 10;
  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });
  const changeSort = (column: string, descending: boolean) =>
    dispatch({ type: 'ORDER', column, descending });

  const openTaskFormModal = () => setShowCreateNewTaskModal(true);
  const closeTaskFormModal = () => setShowCreateNewTaskModal(false);
  const handleShowCreateNewTaskModal = () => {
    setTaskState(undefined);
    openTaskFormModal();
  };

  const handleTaskFormSubmit = (values: OperationTaskForm, resetFormCallback: () => void) => {
    const { title, description, dueDate, status } = values;
    const payload: CreateOperationTask = {
      operationId: id,
      title,
      description,
      dueDate: dueDate || new Date(),
      status,
    };
    // Update task flow
    if (taskState) {
      payload.taskId = taskState.taskId;
    }
    const notifyTitle = payload.taskId ? 'Update Task' : 'Create New Task';
    const taskOperation = payload.taskId
      ? operationService.updateTask
      : operationService.createTask;

    const callback = (taskRes: OperationTask | null) => {
      closeTaskFormModal();
      resetFormCallback();

      if (taskRes) {
        reload();
        notify(notifyTitle, t('successSave'), 'success');
      } else {
        notify(notifyTitle, t('saveFail'), 'danger');
      }
    };

    service(() => taskOperation(payload), { callback, loader: setIsSubmitting });
  };

  const removeDelegateHandler = (idTaskDelegation: number) => {
    setShowTaskDelegateModal(false);
    setShowConfirmDelegate(true);

    setDelegateForRemove(idTaskDelegation);
  };

  const removeDelegate = () => {
    if (delegateForRemove) {
      service(() => operationService.removeTaskDelegation(delegateForRemove), {
        callback: () => {
          reload();
          setShowTaskDelegateModal(true);
        },
      });
    }
  };

  const openTaskDelegateFormModal = () => setShowTaskDelegateModal(true);
  const closeTaskDelegateFormModal = () => setShowTaskDelegateModal(false);
  const handleTaskDelegateFormSubmit = (
    values: OperationTaskDelegateForm,
    resetFormCallback: () => void
  ) => {
    if (!taskState) {
      return;
    }
    const { owner } = values;
    const { taskId } = taskState;
    if (!taskId) {
      return;
    }
    const payload: DelegateOperationTask = {
      operationId: id,
      taskId,
      newOwnerEmail: owner[0],
    };
    loader(true);

    const callback = (taskRes: OperationTask | null) => {
      closeTaskDelegateFormModal();
      resetFormCallback();

      if (taskRes) {
        reload();
        reloadNotifications();
        notify('Delegate Task', t('successSave'), 'success');
      } else {
        loader(false);
        notify('Delegate Task', t('saveFail'), 'danger');
      }
    };

    service(() => operationService.delegateTask(payload), {
      callback,
      loader: (val) => {
        loader(val);
        closeTaskDelegateFormModal();
      },
    });
  };

  const handleShowTaskModal = (task: OperationTask) => {
    const { taskId, title, description, dueDate, status, isAutomatic, code, isOwner } = task;
    const initState: OperationTaskForm = {
      taskId,
      title,
      description,
      dueDate: dueDate === null ? null : new Date(dueDate),
      dueDateStr: dueDate || undefined,
      status,
      code: code || undefined,
      isOwner,
    };
    setTaskState(initState);

    if (isAutomatic) {
      openTaskDelegateFormModal();
    } else {
      openTaskFormModal();
    }
  };

  const [taskSelected, setTaskSelected] = useState<OperationTask | null>(null);
  const showDeleteTaskModal = (task: OperationTask) => setTaskSelected(task);
  const closeDeleteTaskModal = () => setTaskSelected(null);
  const deleteTask = () => {
    if (!taskSelected) {
      return;
    }
    const payload: DeleteOperationTask = {
      operationId: taskSelected.operationId,
      taskId: taskSelected.taskId,
    };
    loader(true);

    const callback = (res: OperationTask | null) => {
      setTaskSelected(null);

      if (res) {
        reload();
        notify('Delete Task', t('successDelete'), 'success');
      } else {
        loader(false);
        notify('Delete Task', t('saveFail'), 'danger');
      }
    };

    service(() => operationService.deleteTask(payload), { callback, loader });
  };

  const hasDelegates = (task: OperationTask) =>
    task.delegates.length > 0 ? (
      <>
        <FontAwesomeIcon icon="user" />
        <FontAwesomeIcon icon="arrow-right" />
        <FontAwesomeIcon icon="user" />
      </>
    ) : (
      <></>
    );

  const cells: Cell<OperationTask>[] = [
    {
      name: 'title',
      column: 'title',
      value: (x) => x.title,
      render: (x, o) => <LinkButton text={x} onClick={() => handleShowTaskModal(o)} />,
      sortDisabled: true,
    },
    { name: 'description', column: 'description', value: (x) => x.description, sortDisabled: true },
    {
      name: 'dueDate',
      column: 'dueDate',
      value: (x) => (dateStr(x.dueDate) || t('na')) as string,
      sortDisabled: true,
    },
    {
      name: 'status',
      column: 'status',
      value: (x) => x.status,
      render: (x, o) =>
        o.isOverDue ? (
          <TaskStatus text={t('taskStatus.OVERDUE')} status="OVERDUE" />
        ) : (
          <TaskStatus text={t(`taskStatus.${x}`)} status={o.status} />
        ),
      sortDisabled: true,
    },
    {
      sortDisabled: true,
      render: (_, o) =>
        !o.isAutomatic ? (
          <LinkButton onClick={() => showDeleteTaskModal(o)} icon="trash-alt" />
        ) : (
          hasDelegates(o)
        ),
    },
  ];

  const renderOverdueTasks = () =>
    data && data.totalTasksOverDue ? (
      <Highlight name={t('overdue')} value={data.totalTasksOverDue} color="red" />
    ) : null;

  return (
    <>
      <div className="details-bar">
        <div className="content">
          <h4>
            {data &&
              data.total > 0 &&
              t('showingTasks', { from: data.from, to: data.to, total: data.total })}
          </h4>
        </div>
        {renderOverdueTasks()}
        {!isAdmin && !isViewer && (
          <div>
            <Button
              key="btnAdd"
              className="btn-secondary"
              text={t('createNewTask')}
              type="button"
              onClick={handleShowCreateNewTaskModal}
            />
            <div className="ml-auto" />
          </div>
        )}
      </div>
      <SpinnerArea loading={loading}>
        {data && data.total > 0 && (
          <>
            <Table
              data={data.list}
              cells={cells}
              keyFunc={(x) => x.taskId}
              sortData={filter}
              changeSort={changeSort}
            />
            <TablePagination
              pageSize={pageSize}
              pageNumber={filter.pageNumber}
              total={data.total}
              onChangePage={changePage}
            />
          </>
        )}
      </SpinnerArea>
      <CustomModal
        title={t(taskState ? 'taskModalTitleUpdate' : 'taskModalTitleCreate', {
          number,
        })}
        visible={showCreateNewTaskModal}
        setVisible={setShowCreateNewTaskModal}
        customClass="modal-dialog--header-task"
      >
        <TaskForm
          formState={taskState}
          handleSubmit={handleTaskFormSubmit}
          handleCancel={closeTaskFormModal}
          isSubmitting={isSubmitting}
        />
      </CustomModal>
      {data && taskState && (
        <>
          <CustomModal
            title={t('taskDetails')}
            visible={showTaskDelegateModal}
            setVisible={setShowTaskDelegateModal}
            customClass="modal-dialog--header-task"
          >
            <TaskDelegateForm
              handleSubmit={handleTaskDelegateFormSubmit}
              handleCancel={closeTaskDelegateFormModal}
              task={taskState}
              operationNumber={number}
              delegates={
                safeFind(data.list, (x) => x.taskId === taskState.taskId).delegates || null
              }
              removeDelegation={removeDelegateHandler}
            />
          </CustomModal>
          <ConfirmModal
            visible={showConfirmRemoveDelegate}
            setVisible={setShowConfirmDelegate}
            action={removeDelegate}
          >
            {t('confirmRemoveDelegation')}
          </ConfirmModal>
        </>
      )}

      <ConfirmModal
        title={t('deleteTask')}
        buttonText={t('delete')}
        visible={taskSelected != null}
        close={closeDeleteTaskModal}
        action={() => deleteTask()}
      >
        {t('deleteTaskConfirmation')}
      </ConfirmModal>
    </>
  );
};

export default Tasks;
